import { useCallback, useState } from 'react';

import {
  Anchor,
  Box,
  Button,
  Card,
  Form,
  FormField,
  FormHelp,
  Heading,
  Icon,
  Input,
  Label,
  List,
  Page,
  Paragraph,
  Select,
  ShowHide,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { IconChevronLeftFilled, IconHouseFilled } from '@zillow/constellation-icons';
import { useWaitlistWebApiHandler_addToGenericWaitlist } from '@zg-rentals/ts-leasing-client-react-query';

import { US_STATES_LABEL_MAP } from '@zg-rentals/states-territories';

import { ZILLOW_LEASES, ZILLOW_TERMS_OF_USE_LINKS } from '../../constants/stringConstants';
import LeasesHeaderNav from '../../components/LeasesHeaderNav';
import { EMAIL_REGEX, WAITLIST_NOTIFICATION_BODY } from '../../constants/stringConstants';

export function GenericWaitlist() {
  const [state, setState] = useState<string>('AL');
  const [city, setCity] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [isEmailValid, setEmailValid] = useState<boolean>(false);
  const [isAddedToWaitlist, setIsAddedToWaitlist] = useState<boolean>(false);
  const { enqueueToast } = useToast();

  const addToGenericWaitlistMutation = useWaitlistWebApiHandler_addToGenericWaitlist();

  const onGetNotifiedSuccessToast = useCallback(() => {
    enqueueToast(<Toast tone="success">{WAITLIST_NOTIFICATION_BODY}</Toast>);
  }, [enqueueToast]);

  function setEmailValue(event: React.ChangeEvent<HTMLInputElement>) {
    setEmailValid(EMAIL_REGEX.test(String(event.target.value).toLowerCase()));
    setEmail(event.target.value);
  }

  function setStateValue(event: React.ChangeEvent<HTMLSelectElement>) {
    setState(event.currentTarget.value);
  }

  function setCityValue(event: React.ChangeEvent<HTMLInputElement>) {
    setCity(event.target.value);
  }

  async function onGetNotifiedClick() {
    await addToGenericWaitlistMutation.mutateAsync({ postData: { email, city, state } });
    setIsAddedToWaitlist(true);
    setEmail('');
    setCity('');
    setState('AL');
    onGetNotifiedSuccessToast();
  }

  return (
    <LeasesHeaderNav>
      <Page.Root css={{ backgroundColor: 'bg.screen.neutral' }}>
        <Page.Breadcrumb>
          <TextButton icon={<IconChevronLeftFilled />} asChild>
            <a href="https://zillow.com/rental-manager/">Back to Zillow Rental Manager</a>
          </TextButton>
        </Page.Breadcrumb>
        <Page.Header>
          <Heading level={1} css={{ marginBottom: 'looser' }}>
            Leases
          </Heading>
        </Page.Header>
        <Page.Content>
          <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'looser' }}>
            <Box css={{ gridColumnEnd: { base: 'span 12', lg: 'span 7' } }}>
              <Heading
                level={2}
                css={{
                  textStyle:
                    'responsive.heading-sm' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
                  marginBottom: 'looser',
                }}
              >
                Get notified when {ZILLOW_LEASES} is available in your area
              </Heading>
              <Form onSubmit={(e) => e.preventDefault()}>
                <FormField
                  required
                  error={!isEmailValid && email.length > 0}
                  label={<Label htmlFor="email-input">Email</Label>}
                  control={
                    <Input
                      id="email-input"
                      value={email}
                      type="email"
                      autoComplete="email"
                      onChange={setEmailValue}
                      fluid={false}
                    />
                  }
                  description={!isEmailValid && email.length > 0 ? <FormHelp>Enter a valid email</FormHelp> : null}
                />
                <FormField
                  label={
                    <Label htmlFor="city-input" optional>
                      City
                    </Label>
                  }
                  control={
                    <Input
                      id="city-input"
                      value={city}
                      autoComplete="address-level2"
                      onChange={setCityValue}
                      fluid={false}
                    />
                  }
                />
                <FormField
                  required
                  label={<Label htmlFor="state-select">State</Label>}
                  control={
                    <Select
                      id="state-select"
                      onChange={setStateValue}
                      autoComplete="address-level1"
                      value={state || ''}
                      fluid={false}
                    >
                      {Object.keys(US_STATES_LABEL_MAP).map((abbreviation) => (
                        <option key={abbreviation} value={abbreviation}>
                          {US_STATES_LABEL_MAP[abbreviation]}
                        </option>
                      ))}
                    </Select>
                  }
                />
                <Button
                  tone="brand"
                  emphasis="filled"
                  css={{ marginBottom: 'looser', marginTop: 'looser' }}
                  onClick={onGetNotifiedClick}
                  disabled={!isEmailValid || isAddedToWaitlist}
                >
                  Get notified
                </Button>
                <Paragraph css={{ textStyle: 'body-xs', color: 'text.subtle' }}>
                  By signing up for Zillow lease updates, you agree to comply with the Zillow{' '}
                  <Anchor target="_blank" href={ZILLOW_TERMS_OF_USE_LINKS.TERMS_OF_USE} rel="noopener noreferrer">
                    Terms of Use
                  </Anchor>
                  .
                </Paragraph>
              </Form>
            </Box>
            <Box css={{ gridColumnEnd: { base: 'span 12', lg: 'span 5' } }}>
              <Card tone="neutral" elevated={false} css={{ padding: 'loose' }}>
                <Icon size="lg" css={{ marginBottom: 'tight' }}>
                  <IconHouseFilled />
                </Icon>
                <Heading
                  level={3}
                  css={{
                    textStyle:
                      'responsive.body-lg-bold' /* oxlint-disable-line constellation/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
                    marginBottom: 'tight',
                  }}
                >
                  {`Where is ${ZILLOW_LEASES} currently available?`}
                </Heading>
                <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <ShowHide.Root>
                    <List.Root>
                      <List.Item>Alabama</List.Item>
                      <List.Item>Alaska</List.Item>
                      <List.Item>Arizona</List.Item>
                      <List.Item>California</List.Item>
                      <List.Item>Colorado</List.Item>
                      <ShowHide.Hidden>
                        <List.Item>Connecticut</List.Item>
                        <List.Item>Delaware</List.Item>
                        <List.Item>Florida</List.Item>
                        <List.Item>Georgia</List.Item>
                        <List.Item>Hawaii</List.Item>
                        <List.Item>Idaho</List.Item>
                        <List.Item>Illinois</List.Item>
                        <List.Item>Indiana</List.Item>
                        <List.Item>Kentucky</List.Item>
                        <List.Item>Michigan</List.Item>
                        <List.Item>Minnesota</List.Item>
                        <List.Item>Mississippi</List.Item>
                        <List.Item>Missouri</List.Item>
                        <List.Item>Nevada</List.Item>
                        <List.Item>New Hampshire</List.Item>
                        <List.Item>New Jersey</List.Item>
                        <List.Item>New Mexico</List.Item>
                        <List.Item>North Carolina</List.Item>
                        <List.Item>Ohio</List.Item>
                        <List.Item>Oklahoma</List.Item>
                        <List.Item>Oregon</List.Item>
                        <List.Item>Pennsylvania</List.Item>
                        <List.Item>Rhode Island</List.Item>
                        <List.Item>South Carolina</List.Item>
                        <List.Item>Tennessee</List.Item>
                        <List.Item>Texas</List.Item>
                        <List.Item>Washington</List.Item>
                        <List.Item>West Virginia</List.Item>
                        <List.Item>Wyoming</List.Item>
                      </ShowHide.Hidden>
                    </List.Root>
                  </ShowHide.Root>
                </Box>
                <TextButton asChild css={{ marginTop: 'looser' }}>
                  <a href="https://www.zillow.com/z/rental-manager/rental-lease-agreements/">Learn more</a>
                </TextButton>
              </Card>
            </Box>
          </Box>
        </Page.Content>
      </Page.Root>
    </LeasesHeaderNav>
  );
}

export default GenericWaitlist;
